/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FunctionComponent } from 'react'
import { Platform, useWindowDimensions } from 'react-native'
import styled, { useTheme } from 'styled-components/native'

import { getTagColors } from 'ui/designSystem/Tag/helper/getTagColors'
import { getTagIcon } from 'ui/designSystem/Tag/helper/getTagIcon'
import { renderTagIcon } from 'ui/designSystem/Tag/helper/renderTagIcon'
import { TagProps, TagVariant } from 'ui/designSystem/Tag/types'
import { Typo } from 'ui/theme'

export const Tag: FunctionComponent<TagProps> = ({
  label,
  variant = TagVariant.DEFAULT,
  Icon,
  ...props
}) => {
  const { fontScale } = useWindowDimensions()
  const theme = useTheme()
  const { background, icon } = theme.designSystem.color

  const FinalIcon = getTagIcon(variant, Icon)

  const { backgroundColor, iconColor, labelColor, iconSize } = getTagColors({
    variant,
    background,
    icon,
    theme,
  })

  const isZoomed = fontScale > 1

  return (
    <Wrapper backgroundColor={backgroundColor} testID="tagWrapper" isZoomed={isZoomed} {...props}>
      {FinalIcon ? (
        <IconContainer>{renderTagIcon(iconColor, iconSize, FinalIcon)}</IconContainer>
      ) : null}
      <LabelText color={labelColor} isZoomed={isZoomed}>
        {label}
      </LabelText>
    </Wrapper>
  )
}

const Wrapper = styled.View<{ backgroundColor: string; isZoomed: boolean }>(
  ({ theme, backgroundColor, isZoomed }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    maxWidth: isZoomed ? '100%' : undefined,
    borderRadius: theme.designSystem.size.borderRadius.s,
    backgroundColor,
    paddingVertical: theme.designSystem.size.spacing.xs,
    paddingHorizontal: theme.designSystem.size.spacing.s,
  })
)

const LabelText = styled(Typo.BodyAccentXs)<{ isZoomed: boolean }>(({ isZoomed }) => ({
  flexShrink: isZoomed ? 1 : undefined,
  ...(Platform.OS === 'web' && { textWrap: 'nowrap' }),
}))

const IconContainer = styled.View(({ theme }) => ({
  marginRight: theme.designSystem.size.spacing.xs,
}))
